import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCard,
  TrendUp,
  Receipt,
  Lightning,
  GearSix,
  ArrowCircleUp,
  Calculator,
  Package,
  Check,
  CheckCircle,
  ShieldCheck,
  Globe,
  ChartBar,
  Clock,
  Sparkle,
  Warning,
  ChatCircleDots,
  PaperPlaneTilt,
  FileText,
  Chair,
  UsersThree,
  Scroll,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { billingApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { cardTitleStyle, dangerBannerClass, bannerTextStyle } from '@/lib/styleKit'
import {
  formatCents,
  formatPerCreditRate,
  creditUsagePercent,
  totalRemainingCredits,
  resolvePlanLabel,
  titleizeKey,
  totalGrantedCredits,
  creditsGranted,
  visibleBillingTabs,
  resolveBillingTab,
} from '@/lib/billingFormat'
import type { BillingTab } from '@/lib/billingFormat'
import CreditConfirmModal from '@/components/billing/CreditConfirmModal'
import PaymentHistoryModal from '@/components/billing/PaymentHistoryModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { usePermission } from '@/hooks/usePermission'
import { useTrialStatus } from '@/hooks/useTrialStatus'
import { useAuthStore } from '@/stores/authStore'
import { livePlan, isDowngradeLocked } from '@/lib/planStatus'
import { PLAN_CARD_FEATURES, PLAN_FEATURE_LABELS } from '@/lib/planFeatures'
import {
  clampSeatPick,
  defaultSeatPick,
  describeSeatUsage,
  extraSeatsPurchasable,
  isPerSeatPlan,
  planSeatsTooFewFor,
  minimumSeatsAfterReduction,
  seatCeilingMessage,
  seatPurchaseTotalCents,
  teamTooBigCopy,
} from '@/lib/seats'
import { useBillingStore } from '../../stores/billingStore'
import type {
  Plan,
  CreditPack,
  EstimateResponse,
  EstimatorCatalogItem,
  BillingCycle,
} from '../../types/billing'

// -----------------------------------------------------------------------------
// Constants — palette per §3 (indigo dominant; amber/emerald/violet/muted only)

const ACTION_LABELS: Record<string, string> = {
  email_generate: 'Email generation',
  contact_enrichment: 'Contact enrichment',
  workflow_step: 'Workflow step',
  presentation_generate: 'Presentation',
  llm_test: 'LLM test',
}

const ACTION_ICONS: Record<string, PhosphorIcon> = {
  email_generate: Sparkle,
  contact_enrichment: Globe,
  workflow_step: ChartBar,
  presentation_generate: Package,
  llm_test: GearSix,
}

// Action tint — soft fill + icon, dual-mode. Indigo-unified per §3 (the action
// type is carried by the icon glyph, not by hue — no rainbow). llm_test stays
// muted as the deliberately de-emphasized diagnostic action.
const ACTION_TINT: Record<string, { wrap: string; icon: string }> = {
  email_generate:        { wrap: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]', icon: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  contact_enrichment:    { wrap: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]', icon: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  workflow_step:         { wrap: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]', icon: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  presentation_generate: { wrap: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]', icon: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  llm_test:              { wrap: 'bg-muted/60 dark:bg-white/[0.04]',         icon: 'text-muted-foreground dark:text-[#8a8f98]' },
}

// Data & enrichment ids/labels here MUST match the "Data & Enrichment" category
// in the Integrations settings page (frontend/src/pages/settings/components/
// IntegrationsSection.tsx, INTEGRATIONS array) — that page is the only place a
// user can actually configure these, so a plan can't advertise a provider that
// isn't there to set up. Providers that ARE implemented backend-side
// (app/services/enrichment/orchestrator.py PROVIDER_METADATA) but have no
// Integrations page entry yet — rocketreach, lusha, cognism, uplead, snov — are
// intentionally left off this list until that UI exists; pageindex/zoominfo
// were previously listed here with no backing provider at all.
const INTEGRATION_LABELS: Record<string, string> = {
  apollo: 'Apollo.io', hunter: 'Hunter.io', peopledatalabs: 'People Data Labs',
  explorium: 'Explorium', fmp: 'Financial Modeling Prep', alpha_vantage: 'Alpha Vantage', newsapi: 'NewsAPI.org',
  salesforce: 'Salesforce', hubspot: 'HubSpot', pipedrive: 'Pipedrive',
  slack: 'Slack', teams: 'MS Teams', zoom: 'Zoom',
  outlook_calendar: 'Outlook Calendar', calendly: 'Calendly',
}

const INTEGRATION_CATEGORIES: Record<string, { label: string; ids: string[] }> = {
  data:          { label: 'Data & enrichment', ids: ['apollo', 'hunter', 'peopledatalabs', 'explorium', 'fmp', 'alpha_vantage', 'newsapi'] },
  crm:           { label: 'CRM',               ids: ['salesforce', 'hubspot', 'pipedrive'] },
  // google_meet dropped from the cards (2026-09-04) the same way Twilio was:
  // the card renders BY CATEGORY, so the id can stay in the Enterprise row's
  // allowed_integrations data (and keep gating the Google service account)
  // without being advertised.
  communication: { label: 'Communication',     ids: ['slack', 'teams', 'zoom'] },
  // google_calendar dropped from the cards (2026-09-04) like google_meet and
  // Twilio: the id stays in the plan rows' data (it still gates the Google
  // service-account credential) but is no longer advertised.
  calendar:      { label: 'Calendar',          ids: ['outlook_calendar', 'calendly'] },
  // No 'Voice & AI' row: Twilio is dropped from the plan cards (2026-09-03).
  // The card renders BY CATEGORY, so an id in a plan's allowed_integrations
  // with no category here is simply not shown — `twilio` can stay in the
  // Enterprise row's data untouched, exactly like zapier/make/n8n already do.
}

const tabs = [
  { key: 'overview',     label: 'Overview',  icon: CreditCard },
  { key: 'usage',        label: 'Usage',     icon: TrendUp },
  { key: 'transactions', label: 'History',   icon: Receipt },
  { key: 'estimator',    label: 'Estimator', icon: Calculator },
] as const

// Pass status chip — border+fill pair, dual-mode. Colour lives in the chip and
// nowhere else: no tinted card surface, no coloured left border (§3).
const STATUS_CHIP: Record<string, string> = {
  active:    'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  past_due:  'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25',
  canceled:  'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
}

const STATUS_DOT: Record<string, string> = {
  active:    'bg-emerald-500',
  past_due:  'bg-amber-500',
  canceled:  'bg-red-500',
}

// The word the user reads for each API status. A raw enum is never rendered
// (§60). "canceled" reads as "Expired" because by the time a user sees it the
// subscription has already run past the month it paid for. The API's three values are the entitlement path's `active` plus
// the two legacy-subscription states.
const STATUS_LABEL: Record<string, string> = {
  active:    'Active',
  past_due:  'Payment failed',
  canceled:  'Expired',
}

// What to tell the user when the subscription is not healthy, keyed by the same status.
const STATUS_EXPLAINER: Record<string, string> = {
  past_due:  'A payment on this plan did not go through, so access may be limited. Update your card or pick a plan to restore it.',
  canceled:  'This plan has ended. Subscribe again to get access back — you are charged monthly and can cancel whenever you like.',
}

/** The per-month price of a plan on a given cycle, in cents. Null means the
 *  plan is not sold on that cycle and must not be offered there. This is the
 *  PER-MONTH figure for both cycles; annual is charged yearly (12x this). */
function planRateCents(plan: Plan, cycle: BillingCycle): number | null {
  // A quoted plan has no self-serve price on either cycle. Its stored amount
  // is 0 (the column is NOT NULL), so returning it would print "$0/month".
  if (plan.is_custom_pricing) return null
  if (cycle === 'annual') return plan.annual_monthly_price_cents ?? null
  return plan.monthly_price_cents
}

/** Whole-percent saving of a plan's annual rate against its monthly rate, or
 *  null when there is nothing to compare or nothing saved. */
function annualSavingPercent(plan: Plan): number | null {
  if (plan.is_custom_pricing) return null
  const annual = plan.annual_monthly_price_cents
  if (!annual || !plan.monthly_price_cents) return null
  if (annual >= plan.monthly_price_cents) return null
  return Math.round((1 - annual / plan.monthly_price_cents) * 100)
}

// Enterprise is quoted rather than listed, and this app has no contact route
// or support address to point at yet — so the CTA states the next step instead
// of pretending to be a purchase. Swap this for a real destination (mailto,
// scheduling link, or an in-app route) when there is one.
const QUOTED_PLAN_HINT =
  'Allocated by our team on request — contact us to set it up or raise your quota.'

const SUPPORT_EMAIL = 'support@beevelope.com'

/** Where "Talk to us" goes. Always available, including while the plan is
 *  active: the point of the button on a quoted plan is not to buy it but to
 *  ask for more quota. */
function quotedPlanMailto(planName: string): string {
  const subject = `${planName} plan — quota request`
  const body = [
    `I'd like to talk about the ${planName} plan.`,
    '',
    'What I need:',
    '',
  ].join('\n')
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

// Shared card chrome
const cardCls = cn(
  'rounded-xl border transition-colors duration-150',
  'bg-card border-border/50',
  'dark:bg-white/[0.02] dark:border-white/[0.08]',
)

// Transaction history pagination: 25 rows per page.
const TRANSACTIONS_PAGE_SIZE = 25

// The estimator's line items are no longer a hardcoded union: they come from
// GET /billing/estimate/catalog, which is generated from the same ACTION_COSTS
// table the charges are computed from. Adding a billable feature to the
// estimator is a backend-only change, and the per-unit price shown beside each
// input cannot disagree with what the action actually costs.
//
// Default slides for the block-priced deck line, matching the backend's
// one-block minimum.
// One priced block (see ACTION_COSTS.presentation_generate.unit_slides),
// so the form opens on the same example the credit table publishes:
// "Deck / collateral (3 slides) — 25".
const DEFAULT_DECK_SLIDES = 3

// Per-line icons, keyed by the catalog's `key`. Presentation only — a key with
// no entry falls back to the generic sparkle, so a newly added backend line
// renders correctly before anyone picks an icon for it.
const ESTIMATOR_ICONS: Record<string, JSX.Element> = {
  emails: <Sparkle className="h-4 w-4" weight="regular" />,
  enrichments: <Globe className="h-4 w-4" weight="regular" />,
  presentations: <Package className="h-4 w-4" weight="regular" />,
  content_insights: <ChartBar className="h-4 w-4" weight="regular" />,
  reply_classifications: <ChatCircleDots className="h-4 w-4" weight="regular" />,
  auto_replies: <PaperPlaneTilt className="h-4 w-4" weight="regular" />,
  text_transforms: <Sparkle className="h-4 w-4" weight="regular" />,
  materials_processed: <FileText className="h-4 w-4" weight="regular" />,
  workflow_steps: <ChartBar className="h-4 w-4" weight="regular" />,
}

// Eyebrow label
const eyebrowStyle = { fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' } as const

export default function BillingPage() {
  const {
    plans, subscription, credits, creditPacks, transactions, transactionsTotal,
    summary, dailyUsage, breakdown, trends,
    fetchPlans, fetchSubscription, fetchCredits, fetchCreditPacks,
    fetchTransactions, fetchSummary, fetchDailyUsage, fetchBreakdown,
    fetchTrends,
    switchPlan,
    teamUsage, fetchTeamUsage,
    addSeats, reduceSeats, cancelSeatReduction,
  } = useBillingStore()

  // The account's active-seat count, for scaling a plan's per-seat credit
  // rate up to the true monthly grant (EntitlementService does the same
  // multiplication server-side at grant time).
  const seatCount = subscription?.seat_count ?? 1
  // P vs U for the plan they hold (Momentum per-seat spec §5). Absent for a
  // single-seat plan or an older payload.
  const seatStatus = subscription?.seats ?? null

  // Owner-only per the matrix ("manage" on both cells); Admin is "view" (sees
  // this page, can't purchase). Manager/Rep/Analyst have no billing cell at
  // all — plans, payment and purchasing stay invisible to them (the API 403s
  // the subscription/settings endpoints for those roles too).
  // Payment history is opened from the header icon and fetches its own rows.
  const [showPaymentHistory, setShowPaymentHistory] = useState(false)
  const { canWrite: canBuyCredits } = usePermission('billing', 'buy_credits')
  const { canWrite: canManageSubscription, allowed: billingAllowed } = usePermission('billing', 'manage_subscription')
  const { isTrial, limits: trialLimits } = useTrialStatus()
  // The per-teammate usage table is a per_user_credit_budgets feature
  // (Momentum/Enterprise/trial). Read off the same plan-status list the rest
  // of the app locks features from, so Starter never even asks and never
  // sees the gate's 403.
  const planFeatures = useAuthStore((s) => s.planStatus?.features ?? null)
  const planHasTeamUsage = planFeatures?.includes('per_user_credit_budgets') ?? false

  // The reporting half of this page (credit spend + history) is a separate
  // grant: `credit_budgets.view_usage`, which Manager and Analyst hold
  // read-only and which reads the WHOLE account's ledger for them. It's what
  // lets an Analyst reconcile campaign numbers on Analytics against what the
  // team actually spent. "own" (Rep) still means their own rows only.
  // A Rep's "own" scope is not a reporting surface (their own ledger is a
  // handful of rows and they can't act on it), so this half of the page opens
  // for the account-wide holders only.
  const { allowed: usageAllowed, scope: usageScope } = usePermission('credit_budgets', 'view_usage')
  const teamWideUsage = usageAllowed && usageScope !== 'own'
  // Memoised because `visibleTabKeys` is an effect dependency below: a fresh
  // array identity on every render would re-run that effect on every render.
  const visibleTabKeys = useMemo(
    () => visibleBillingTabs(billingAllowed, teamWideUsage),
    [billingAllowed, teamWideUsage],
  )
  const visibleTabs = useMemo(
    () => tabs.filter((t) => (visibleTabKeys as readonly string[]).includes(t.key)),
    [visibleTabKeys],
  )

  const location = useLocation()
  const navigate = useNavigate()
  // Always opens on Overview. This used to be `billingAllowed ? 'overview' :
  // 'usage'`, which sent an Owner to Usage on every fresh load: the permission
  // map is fetched asynchronously, so `billingAllowed` is false for the first
  // render or two, and a useState initialiser only runs once — the permissions
  // landing a moment later never revised the choice. Usage-only roles are
  // corrected off Overview by the effect below, once we actually know the role.
  const [activeTab, setActiveTab] = useState<BillingTab>('overview')
  const [estimatorInputs, setEstimatorInputs] = useState<Record<string, number>>({})
  const [avgDeckSlides, setAvgDeckSlides] = useState<number>(DEFAULT_DECK_SLIDES)
  const [estimatorCatalog, setEstimatorCatalog] = useState<EstimatorCatalogItem[]>([])
  const [catalogError, setCatalogError] = useState(false)
  const [estimateResult, setEstimateResult] = useState<EstimateResponse | null>(null)
  const [isEstimating, setIsEstimating] = useState(false)
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null)
  // Per-pack pending flag. The store's shared `isLoading` used to drive every
  // pack button at once, so clicking one spun all three.
  const [purchasingPackId, setPurchasingPackId] = useState<number | null>(null)
  // Transaction history is paginated (25/page); track the current 1-based page.
  const [transactionsPage, setTransactionsPage] = useState(1)
  // Which rate the plan grid is showing. Starts null rather than 'monthly' so
  // an annual customer never sees the wrong tab flash before /subscription
  // lands; the effect below seeds it once, and after that it is the
  // customer's — a background refetch must not yank them off a tab they chose.
  const [planCycle, setPlanCycle] = useState<BillingCycle | null>(null)
  // Plan "armed" state: set on whole-card hover, cleared on card mouseLeave.
  // Drives the value-prop ribbon + tint + filled CTA per plan.
  const [armedPlanId, setArmedPlanId] = useState<number | null>(null)
  // Nothing spends money without a confirmation step. Exactly one of these is
  // ever set; whichever it is drives the single CreditConfirmModal below.
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null)
  const [pendingPack, setPendingPack] = useState<CreditPack | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  // Cancelling is confirmed on its own, never inline.
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  // Momentum per-seat spec §4: how many seats the pending plan is bought
  // with. Seeded when the confirm modal opens for a per-seat plan; ignored
  // (and not sent) for a single-seat one.
  const [pendingSeats, setPendingSeats] = useState<number>(2)
  // Mid-cycle seat changes on the CURRENT plan (spec §5/§7/§8). Exactly one
  // is open at a time; each holds the number being confirmed.
  const [buyingSeats, setBuyingSeats] = useState<number | null>(null)
  const [reducingTo, setReducingTo] = useState<number | null>(null)
  const [seatBusy, setSeatBusy] = useState(false)

  // Navigate the transaction history to a 1-based page (clamped to range).
  const goToTransactionsPage = (page: number) => {
    const totalPages = Math.max(1, Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE))
    const next = Math.min(Math.max(1, page), totalPages)
    setTransactionsPage(next)
    fetchTransactions((next - 1) * TRANSACTIONS_PAGE_SIZE, TRANSACTIONS_PAGE_SIZE)
  }

  // Keyed on `pathname`, not the whole `location`: the post-checkout poll below
  // rewrites `location.search`, which used to re-run this on an unrelated change.
  useEffect(() => {
    if (location.pathname === '/billing/estimator' && billingAllowed) setActiveTab('estimator')
  }, [location.pathname, billingAllowed])

  // Correct the opening tab once the permission map has actually loaded — a
  // usage-only role has no Overview to sit on. Holds off entirely while the
  // visible set is empty (permissions still in flight), so the default is
  // never "corrected" on the strength of a not-yet-known answer, and re-runs
  // if the role's access changes mid-session.
  useEffect(() => {
    const corrected = resolveBillingTab(activeTab, visibleTabKeys)
    if (corrected) setActiveTab(corrected)
  }, [activeTab, visibleTabKeys])

  useEffect(() => {
    // Fetch only what this role may read: plans/subscription/packs need the
    // billing cell (the API 403s them otherwise), while balance, history and
    // usage need only the reporting cell. A role with neither gets the access
    // notice below and fires nothing at all.
    if (billingAllowed) {
      fetchPlans()
      fetchSubscription()
      fetchCreditPacks()
    }
    if (billingAllowed || teamWideUsage) {
      fetchCredits()
      fetchTransactions(0, TRANSACTIONS_PAGE_SIZE)
      fetchSummary()
    }
    // The per-teammate table is for the account-wide readers only (Analyst,
    // Owner, Admin); a Rep's or Manager's own row is not a team view -- and
    // only on a plan that has the feature at all.
    if (teamWideUsage && planHasTeamUsage) fetchTeamUsage()
  }, [billingAllowed, teamWideUsage, planHasTeamUsage])

  // After returning from Stripe Checkout (?session_id=…), the subscription is
  // created by an async webhook that may not have landed yet. Poll for a short
  // window so the newly-bought plan appears without a manual refresh, then strip
  // the query param. Detects a change vs. the plan shown before checkout, which
  // covers both a first subscription and switching to a different tier.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.get('session_id')) return

    // Baseline is the plan that was RUNNING before checkout (null when it had
    // expired), so re-subscribing to the tier you just lost still registers as
    // a change rather than polling out the full 10 attempts.
    const baselinePlanId = livePlan(useBillingStore.getState().subscription)?.plan.id ?? null
    const baselineQueuedId = useBillingStore.getState().subscription?.queued_plan?.plan.id ?? null
    let attempts = 0
    let cancelled = false
    let timer: number | undefined

    const poll = async () => {
      attempts += 1
      await Promise.all([fetchSubscription(), fetchCredits()])
      if (cancelled) return
      const current = useBillingStore.getState().subscription
      const live = livePlan(current)
      // A queued lower plan does not change the LIVE plan (the current one keeps
      // running), so also resolve when a newly queued plan appears — otherwise
      // returning from a queue checkout would poll out all 10 attempts.
      const reflected =
        (live && live.plan.id !== baselinePlanId)
        || (current?.queued_plan != null && current.queued_plan.plan.id !== baselineQueuedId)
      if (reflected || attempts >= 10) {
        navigate(location.pathname, { replace: true })
        return
      }
      timer = window.setTimeout(poll, 2000)
    }
    poll()

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
     
  }, [location.search])

  // Open the grid on the rate they are actually paying, once the subscription
  // has loaded. Guarded on still being unset so this can never fight a click.
  useEffect(() => {
    if (planCycle !== null) return
    const live = livePlan(subscription)
    if (live) setPlanCycle(live.billing_cycle === 'annual' ? 'annual' : 'monthly')
  }, [subscription])

  // Deep-link from the sidebar "Upgrade" action → scroll to the Plans section.
  useEffect(() => {
    if ((location.state as any)?.scrollTo !== 'plans') return
    const t = window.setTimeout(() => {
      document.getElementById('billing-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 250)
    return () => window.clearTimeout(t)
  }, [location.state])

  useEffect(() => {
    if (activeTab === 'usage') {
      fetchDailyUsage()
      fetchBreakdown()
      fetchTrends()
    }
  }, [activeTab])

  // Two separate questions, and folding them into one used to hide a
  // customer's own plan from them (bug 22364): an account holding Standard
  // with no renewal agreement behind it — a plan provisioned directly, or
  // bought before plans became subscriptions — saw all three plans, Standard
  // included, offered as if they had never subscribed, with no Cancel and
  // nothing marked current.
  //
  // "Is a plan running right now?" An entitlement is live until it expires,
  // whether or not anything is lined up to renew it. `/subscription` also
  // reports the pass that most recently ran out (so the card below can say
  // "Expired on <date>"), and that one is emphatically not live — see
  // lib/planStatus for why the date check cannot be skipped.
  const hasLivePlan = Boolean(livePlan(subscription))
  // "Will it renew?" Only a plan with a recurring agreement behind it has
  // anything to cancel; without one it simply runs out on its expiry date, and
  // the way forward is to start the subscription, not to cancel a promise
  // nobody made. `auto_renew` is false both for a cancelled agreement winding
  // down and for a plan that never had one.
  const planRenews = Boolean(subscription?.auto_renew)

  // `/subscription` returns `current_period_start: null` when the entitlement has
  // no `activated_at`, and `new Date(null)` is the epoch — this used to print
  // "1/1/1970". Anything unparseable renders an em dash instead.
  const fmtDate = (iso: string | null | undefined) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Load the price list the estimator is built from. Deferred until the tab is
  // actually opened — most visits to /billing never reach the estimator, and
  // this is the one request on the page nothing else needs.
  useEffect(() => {
    if (activeTab !== 'estimator' || estimatorCatalog.length > 0) return
    let cancelled = false
    billingApi
      .estimateCatalog()
      .then((res) => {
        if (!cancelled) {
          setEstimatorCatalog(res.data.items)
          setCatalogError(false)
        }
      })
      .catch(() => {
        if (!cancelled) setCatalogError(true)
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, estimatorCatalog.length])

  const handleEstimate = async () => {
    setIsEstimating(true)
    try {
      const result = await useBillingStore.getState().estimate({
        counts: estimatorInputs,
        avg_deck_slides: avgDeckSlides,
      })
      setEstimateResult(result)
    } finally {
      setIsEstimating(false)
    }
  }

  // Meter denominator = total credits GRANTED, so it agrees with the "Total
  // available" figure printed beside it. The old formula divided by
  // `included + purchased` and ignored `extra_credits`, showing "95% used" to a
  // user sitting on thousands of extra credits.
  const usagePercent = creditUsagePercent(credits)
  const totalRemaining = credits ? totalRemainingCredits(credits) : 0
  const outOfCredits = credits !== null && totalRemaining <= 0

  // Highlight the best-value pack: the middle tier by credit count (largest marginal
  // discount per dollar). Computed from the data so it survives backend reordering.
  const recommendedPackId = creditPacks?.length
    ? [...creditPacks].sort((a, b) => a.credits - b.credits)[Math.floor(creditPacks.length / 2)]?.id ?? null
    : null


  const scrollToId = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Top-up CTA target: the packs grid when it is rendered (it needs an active
  // plan), otherwise the plans grid — subscribing is how a user with no plan
  // gets credits.
  const goToTopUp = () => {
    setActiveTab('overview')
    window.setTimeout(() => scrollToId(hasLivePlan ? 'billing-credit-packs' : 'billing-plans'), 0)
  }

  const goToPlans = () => {
    setActiveTab('overview')
    window.setTimeout(() => scrollToId('billing-plans'), 0)
  }

  // Selecting a plan opens the confirmation. Confirming either redirects to
  // Stripe Checkout (a monthly subscription), resumes a subscription that was
  // winding down when the same plan is picked again.
  const confirmPlanSwitch = async () => {
    const plan = pendingPlan
    if (!plan) return
    setConfirmBusy(true)
    setLoadingPlanId(plan.id)
    try {
      // A plan the user still holds time on is a free reorder; anything else
      // opens a monthly subscription. The backend makes the same distinction.
      // A per-seat plan carries the seat count chosen in the modal; a
      // single-seat plan sends none (the server treats that as one).
      await switchPlan(plan.id, activeCycle, isPerSeatPlan(plan) ? pendingSeats : undefined)
      setPendingPlan(null)
    } finally {
      setConfirmBusy(false)
      setLoadingPlanId(null)
    }
  }

  // Opening the plan confirmation seeds the seat picker: the plan minimum,
  // or the people already on the team if that is more (the server refuses a
  // purchase that would leave someone without a paid seat).
  const openPlanConfirm = (plan: Plan) => {
    if (isPerSeatPlan(plan)) {
      setPendingSeats(defaultSeatPick(plan, seatStatus?.committed_seats ?? seatCount))
    }
    setPendingPlan(plan)
  }

  const confirmBuySeats = async () => {
    if (!buyingSeats) return
    setSeatBusy(true)
    try {
      if (await addSeats(buyingSeats)) setBuyingSeats(null)
    } finally {
      setSeatBusy(false)
    }
  }

  const confirmReduceSeats = async () => {
    if (!reducingTo) return
    setSeatBusy(true)
    try {
      if (await reduceSeats(reducingTo)) setReducingTo(null)
    } finally {
      setSeatBusy(false)
    }
  }

  const undoSeatReduction = async () => {
    setSeatBusy(true)
    try {
      await cancelSeatReduction()
    } finally {
      setSeatBusy(false)
    }
  }

  const confirmPackPurchase = async () => {
    const pack = pendingPack
    if (!pack) return
    setConfirmBusy(true)
    setPurchasingPackId(pack.id)
    try {
      await useBillingStore.getState().purchaseCredits(pack.id)
      setPendingPack(null)
    } finally {
      setConfirmBusy(false)
      setPurchasingPackId(null)
    }
  }

  // Re-picking the plan they already hold is a renewal, not a switch: the
  // backend anchors the first charge to the end of the time they already paid
  // for (or revives a cancelled agreement), so the "replaces your current
  // plan" copy below would be wrong for it.
  // The tab in force. Falls back to the billed rate, then monthly, so this is
  // never null at the point anything is charged.
  const activeCycle: BillingCycle =
    planCycle ?? (livePlan(subscription)?.billing_cycle === 'annual' ? 'annual' : 'monthly')

  // End of the annual term the customer has already paid for. `subscription
  // .current_period_end` is the ENTITLEMENT's 30-day slot, which on annual is
  // a month out, not a year — so this is the only date in the payload that
  // says when an annual plan actually stops. Null on monthly, and null once
  // it is in the past.
  const commitmentEndsAt = (() => {
    const raw = subscription?.commitment_ends_at
    if (!raw) return null
    const ms = new Date(raw).getTime()
    return Number.isNaN(ms) || ms <= Date.now() ? null : raw
  })()

  // Headline saving for the Annual tab badge: the best on offer, so the badge
  // is never a claim no plan actually delivers.
  const maxAnnualSaving = (() => {
    const savings = plans.map(annualSavingPercent).filter((v): v is number => v !== null)
    return savings.length ? Math.max(...savings) : null
  })()

  // Plans on offer for the selected cycle. A quoted plan has no rate but must
  // still be listed — "Talk to us" is the whole point of it being on the page.
  const visiblePlans = plans.filter(
    (p) => p.is_custom_pricing || planRateCents(p, activeCycle) !== null,
  )

  // What the plan they hold costs ON THE SELECTED CYCLE — what Upgrade/
  // Downgrade has to be measured against, so a cheaper annual rate for a
  // bigger plan is still labelled an upgrade.
  const currentPlanRateCents = hasLivePlan && subscription
    ? planRateCents(subscription.plan, activeCycle)
    : null

  // Plan TIER, for the no-downgrade rule: while the current plan is still
  // renewing, a lower-tier plan cannot be taken — the customer cancels first,
  // then the smaller plan takes over at expiry. Tier is monthly_price_cents
  // (Starter < Momentum < Enterprise), not the cycle rate, so the lock reads
  // the same on both the monthly and annual tabs and matches the server guard
  // in /switch-plan. Null when there is no live plan (nothing to downgrade from).
  const currentPlanTierCents = hasLivePlan && subscription
    ? subscription.plan.monthly_price_cents
    : null

  // A lower plan lined up to start when the current one expires (the
  // no-downgrade flow). Null when nothing is queued.
  const queuedPlan = subscription?.queued_plan ?? null

  // Team size for the single-seat rule: a single-seat plan (Starter) cannot be
  // taken while the team has 2+ people. Members plus pending invites, matching
  // the server's committed-seats count.
  const teamCommitted = seatStatus?.committed_seats ?? seatCount

  // Re-picking the plan they hold is a renewal only when the CYCLE matches too.
  // Across cycles it is a re-rate: /switch-plan anchors the first charge to the
  // end of the month already paid for, so nothing is charged today.
  const pendingIsCurrentPlan = Boolean(
    hasLivePlan
    && subscription
    && pendingPlan
    && subscription.plan.id === pendingPlan.id
    && subscription.billing_cycle === activeCycle,
  )

  // Taking a lower plan while the current one is winding down (cancelled but
  // still running): it QUEUES behind the current plan and starts at expiry.
  // The current plan is not renewing here (the no-downgrade lock only opens the
  // lower tiers once it is cancelled), so this is the queue path.
  const pendingIsQueued = Boolean(
    pendingPlan
    && hasLivePlan
    && !planRenews
    && currentPlanTierCents !== null
    && pendingPlan.monthly_price_cents < currentPlanTierCents,
  )

  // One modal, driven by whichever purchase is pending.
  const pendingIsPerSeat = pendingPlan !== null && isPerSeatPlan(pendingPlan)
  const pendingRateCents = pendingPlan
    ? (planRateCents(pendingPlan, activeCycle) ?? pendingPlan.monthly_price_cents)
    : 0
  const pendingCommitted = seatStatus?.committed_seats ?? seatCount
  const pendingPurchase = pendingPlan
    ? {
          actionLabel: pendingIsPerSeat
            ? `${pendingPlan.display_name} — ${pendingSeats} seat${pendingSeats === 1 ? '' : 's'}, ${activeCycle === 'annual' ? 'annual plan' : 'monthly'}`
            : `${pendingPlan.display_name} — ${activeCycle === 'annual' ? 'annual plan' : 'monthly'}`,
          // Per-seat plans charge rate x seats (Momentum per-seat spec §3).
          // Annual is charged once for the whole year, so the headline price is
          // the yearly total (12 months), not the per-month figure.
          priceCents: (
            (pendingIsPerSeat
              ? seatPurchaseTotalCents(pendingRateCents, pendingSeats)
              : pendingRateCents)
            * (activeCycle === 'annual' ? 12 : 1)
          ),
          recurringInterval: activeCycle === 'annual' ? ('year' as const) : ('month' as const),
          // What the buyer's OWN wallet gets. A per-user-wallet plan grants
          // this to every seat separately (never a pooled multiple); a
          // pooled per-seat plan (Enterprise) multiplies; a listed plan is
          // the account figure as printed.
          grantedCredits: pendingPlan.credits_per_user_wallet
            ? pendingPlan.included_credits
            : pendingPlan.credits_are_per_seat
              ? totalGrantedCredits(pendingPlan.included_credits, seatCount)
              : pendingPlan.included_credits,
          confirmLabel: pendingIsQueued ? 'Queue this plan' : 'Continue to payment',
          recurring: true,
          detail: (
            <>
              {pendingIsPerSeat && (
                // The "how many users (seats) do you need?" step (spec §4),
                // inside the confirmation so the price above always reflects
                // the count. 1 -> Starter and 6+ -> Enterprise are said out
                // loud rather than silently clamped away.
                <div className="mb-3 space-y-2" data-testid="seat-picker">
                  <label htmlFor="plan-seats" className="block text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', fontWeight: 510 }}>
                    How many users (seats) do you need?
                  </label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label="Fewer seats"
                      disabled={pendingSeats <= clampSeatPick(pendingPlan, 1, pendingCommitted)}
                      onClick={() => setPendingSeats((n) => clampSeatPick(pendingPlan, n - 1, pendingCommitted))}
                    >
                      −
                    </Button>
                    <Input
                      id="plan-seats"
                      type="number"
                      min={pendingPlan.seat_min}
                      max={pendingPlan.seat_max ?? undefined}
                      value={pendingSeats}
                      onChange={(e) => setPendingSeats(clampSeatPick(pendingPlan, parseInt(e.target.value, 10), pendingCommitted))}
                      className="w-20 text-center tabular-nums"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label="More seats"
                      disabled={pendingPlan.seat_max !== null && pendingSeats >= pendingPlan.seat_max}
                      onClick={() => setPendingSeats((n) => clampSeatPick(pendingPlan, n + 1, pendingCommitted))}
                    >
                      +
                    </Button>
                    <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums" style={{ fontSize: '12px' }}>
                      {activeCycle === 'annual'
                        ? `${formatCents(pendingRateCents)} × ${pendingSeats} × 12 = ${formatCents(seatPurchaseTotalCents(pendingRateCents, pendingSeats) * 12)}/year`
                        : `${formatCents(pendingRateCents)} × ${pendingSeats} = ${formatCents(seatPurchaseTotalCents(pendingRateCents, pendingSeats))}/month`}
                    </span>
                  </div>
                  <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11px', lineHeight: 1.5 }}>
                    {pendingPlan.display_name} is for {pendingPlan.seat_min}–{pendingPlan.seat_max} users. Credits are pooled:
                    {' '}{pendingPlan.included_credits.toLocaleString()} per seat, {totalGrantedCredits(pendingPlan.included_credits, pendingSeats).toLocaleString()} a month
                    for the team; contacts are shared at {pendingPlan.features.max_contacts.toLocaleString()} for the whole team.
                    {pendingCommitted > pendingPlan.seat_min && (
                      <> You already have {pendingCommitted} people on the team, so that is the fewest seats you can buy.</>
                    )}
                    {' '}{pendingPlan.seat_min > 2 ? 'Smaller team? Momentum fits 2–5 users. ' : 'Just one user? Starter or Basic is the plan for you. '}
                    {pendingPlan.name === 'enterprise'
                      ? `More than ${pendingPlan.seat_max}? Contact sales for a custom plan.`
                      : `More than ${pendingPlan.seat_max}? Upgrade to Enterprise.`}
                  </p>
                </div>
              )}
              {pendingIsQueued
                ? `This plan is queued — it starts when your current plan ends${subscription ? ` on ${fmtDate(subscription.current_period_end)}` : ''}. Nothing is charged today; the first charge lands then.`
                : pendingIsCurrentPlan
                ? 'You keep the days you have already paid for — the first monthly charge lands when they run out.'
                : hasLivePlan
                  ? 'The new plan starts immediately and replaces your current one — from now on the charge is for the new plan.'
                  : activeCycle === 'annual'
                    ? 'Charged once for the year; your credits refresh every month across it.'
                    : null}
            </>
          ),
          onConfirm: confirmPlanSwitch,
        }
    : pendingPack
      ? {
          actionLabel: `${pendingPack.credits.toLocaleString()} extra credits`,
          priceCents: pendingPack.price_cents,
          grantedCredits: pendingPack.credits,
          confirmLabel: 'Continue to payment',
          recurring: false,
          detail: 'Credits land on your balance as soon as payment clears.',
          onConfirm: confirmPackPurchase,
        }
      : null

  const closeConfirm = () => {
    if (confirmBusy) return
    setPendingPlan(null)
    setPendingPack(null)
  }

  // Turning auto-renew off. The plan is NOT ended here — Stripe stops charging
  // and the entitlement already paid for runs to its expiry date.
  const handleCancelRenewal = async () => {
    setCancelBusy(true)
    try {
      await useBillingStore.getState().cancelSubscription()
      setConfirmingCancel(false)
    } catch {
      // Store toasts the failure; keep the dialog open so it can be retried.
    } finally {
      setCancelBusy(false)
    }
  }

  // Resuming goes through the normal plan CTA: /switch-plan detects the
  // winding-down agreement for that same plan and revives it rather than
  // opening a second subscription.

  // A role with neither the billing cell nor the reporting cell has nothing
  // to see here; deep links (e.g. the dashboard credit card) land here, so
  // show a quiet notice instead of a page of failed requests.
  if (!billingAllowed && !teamWideUsage) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '18px', fontWeight: 590, letterSpacing: '-0.2px' }}
        >
          Billing is managed by your account owner
        </h1>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-2 max-w-md"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Your role doesn&apos;t include access to plans, payments, or billing
          settings. Contact your workspace Owner or Admin for anything
          billing-related.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header — §23 */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
          >
            Billing
          </h1>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
          >
            {billingAllowed
              ? 'Manage your plan, credits, and usage.'
              : "Your team's credit usage and spend history."}
          </p>
        </div>

        {/* Payment history — gated on the same permission as the endpoint
            (billing.manage_subscription: Owner manage / Admin view), so the
            icon is never offered to someone who would get a 403 on click. */}
        {billingAllowed && (
          <button
            type="button"
            onClick={() => setShowPaymentHistory(true)}
            title="Payment history"
            aria-label="Open payment history"
            aria-haspopup="dialog"
            className={cn(
              'shrink-0 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 transition-colors',
              'border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted',
              'dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.06]',
            )}
            style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
          >
            <Scroll className="h-4 w-4" weight="regular" />
            <span className="max-sm:sr-only">Invoices</span>
          </button>
        )}
      </div>

      <PaymentHistoryModal
        isOpen={showPaymentHistory}
        onClose={() => setShowPaymentHistory(false)}
      />

      {/* Tab Navigation — indigo brand active border */}
      <div className="flex flex-wrap gap-1 border-b border-border/50 dark:border-white/[0.06]">
        {visibleTabs.map(({ key, label, icon: Icon }) => {
          const active = activeTab === key
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors duration-150 whitespace-nowrap -mb-px',
                active
                  ? 'border-[#5e6ad2] text-[#5e6ad2] dark:border-[#7170ff] dark:text-[#a5b4fc]'
                  : 'border-transparent text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]',
              )}
              style={{ fontSize: '13px', fontWeight: active ? 590 : 510, letterSpacing: '-0.13px' }}
            >
              <Icon className="h-3.5 w-3.5" weight={active ? 'bold' : 'regular'} />
              {label}
            </button>
          )
        })}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {/* billingAllowed as well as the tab: 'overview' is the initial state
          and the permission map lands a render or two later, so without this
          the plan cards are briefly offered to a role whose only possible
          action on them is a 403 from POST /billing/switch-plan. */}
      {activeTab === 'overview' && billingAllowed && (
        <div className="space-y-5">
          {/* Credit Balance */}
          {credits && (
            <Card className={cardCls} padding="lg">
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="h-10 w-10 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30"
                  aria-hidden="true"
                >
                  <Lightning className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                </div>
                <div>
                  <h2
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                  >
                    Credit balance
                  </h2>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    Current billing month
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {[
                  // The headline number is everything spendable (plan + extra
                  // wallet) — plan-only here read "163" while the user had
                  // thousands of purchased extra credits.
                  { label: 'Remaining', value: totalRemainingCredits(credits), highlight: true },
                  { label: 'Included',  value: credits.included_credits },
                  // Hidden: purchased_credits is always 0 — top-ups land in Extra credits.
                  // { label: 'Purchased', value: credits.purchased_credits },
                  { label: 'Used',      value: credits.used_credits },
                  { label: 'Extra credits', value: credits.extra_credits ?? 0, extra: true },
                ].map(({ label, value, highlight, extra }) => (
                  <div
                    key={label}
                    className={cn(
                      'rounded-lg p-3 border',
                      extra
                        ? 'bg-emerald-500/[0.06] border-emerald-500/25 dark:bg-emerald-500/[0.10] dark:border-emerald-500/25'
                        : 'bg-muted/30 border-border/50 dark:bg-white/[0.02] dark:border-white/[0.06]',
                    )}
                  >
                    <p
                      className={cn(
                        'uppercase mb-1',
                        extra ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground dark:text-[#8a8f98]',
                      )}
                      style={eyebrowStyle}
                    >
                      {label}
                    </p>
                    <p
                      className={cn(
                        'tabular-nums',
                        extra
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : highlight
                            ? 'text-foreground dark:text-[#f7f8f8]'
                            : 'text-foreground/80 dark:text-[#d0d6e0]',
                      )}
                      style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.1 }}
                    >
                      {value.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              {/* Combined spendable total: plan remaining plus extra wallet. */}
              <p
                className="text-foreground dark:text-[#f7f8f8] mb-3 tabular-nums"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Total available:{' '}
                <span style={{ fontWeight: 590 }}>{totalRemaining.toLocaleString()}</span>{' '}
                credits
                {(credits.extra_credits ?? 0) > 0 && (
                  <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontWeight: 400 }}>
                    {' '}({credits.remaining_credits.toLocaleString()} plan + {(credits.extra_credits ?? 0).toLocaleString()} extra)
                  </span>
                )}
              </p>

              {/* Progress bar — semantic tint based on usage */}
              <div
                className="w-full h-2 rounded-full overflow-hidden bg-muted/50 dark:bg-white/[0.06]"
                role="progressbar"
                aria-valuenow={usagePercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Credits used"
              >
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-amber-500' : 'bg-emerald-500',
                  )}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-1.5 tabular-nums"
                style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
              >
                {usagePercent}% of your granted credits used
              </p>

              {/* Out of credits — the state the UI had no rendering for at all.
                  Colour stays in the banner's own chrome; the card is untinted. */}
              {outOfCredits && (
                <div className={cn(dangerBannerClass, 'mt-4')} role="status" aria-live="polite">
                  <Warning className="h-4 w-4 shrink-0 mt-0.5" weight="regular" aria-hidden="true" />
                  <div className="min-w-0">
                    <p style={{ ...bannerTextStyle, fontWeight: 590 }}>You are out of credits</p>
                    <p style={bannerTextStyle}>
                      Sends, generations and enrichments will fail until you top up.
                    </p>
                    {canBuyCredits && (
                      <Button
                        variant="default"
                        size="sm"
                        className="mt-2"
                        onClick={goToTopUp}
                        icon={<Package className="h-4 w-4" weight="regular" />}
                      >
                        Buy credits
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Current plan — status, the month it covers, and what happens when
              that month ends: renews, or (once cancelled) expires. Cancelling
              itself lives on the plan card below, next to the plan it ends. */}
          {subscription && (
            <Card className={cardCls} padding="lg">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="h-10 w-10 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 flex-shrink-0"
                    aria-hidden="true"
                  >
                    <ShieldCheck className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                  </div>
                  <div className="min-w-0">
                    <h2
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                    >
                      Current plan
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border uppercase',
                          STATUS_CHIP[subscription.status]
                            || 'bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]',
                        )}
                        style={{ fontSize: '9px', fontWeight: 590, letterSpacing: '0.06em' }}
                      >
                        <span
                          className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[subscription.status] || 'bg-muted-foreground')}
                          aria-hidden="true"
                        />
                        {STATUS_LABEL[subscription.status] || titleizeKey(subscription.status)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    {subscription.plan.display_name}
                  </p>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                    style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                  >
                    {fmtDate(subscription.current_period_start)} — {fmtDate(subscription.current_period_end)}
                  </p>
                </div>
              </div>
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.55 }}
              >
                {STATUS_EXPLAINER[subscription.status] || (
                  subscription.auto_renew ? (
                    <>
                      Renews automatically on{' '}
                      <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
                        {fmtDate(subscription.current_period_end)}
                      </span>
                      {' '}for{' '}
                      {formatCents(seatPurchaseTotalCents(
                        (subscription.billing_cycle === 'annual'
                          ? subscription.plan.annual_monthly_price_cents
                          : subscription.plan.monthly_price_cents) ?? 0,
                        seatStatus?.per_seat_billing ? (seatStatus.pending_paid_seats ?? seatStatus.paid_seats) : 1,
                      ))}
                      /month
                      {seatStatus?.per_seat_billing && (
                        <> ({seatStatus.pending_paid_seats ?? seatStatus.paid_seats} seat{(seatStatus.pending_paid_seats ?? seatStatus.paid_seats) === 1 ? '' : 's'})</>
                      )}
                      , until you cancel.
                    </>
                  ) : (
                    commitmentEndsAt ? (
                      // Cancelled annual plan: the whole year was already paid
                      // up front, so there are no more charges — it simply runs
                      // to the end of the paid year, with credits refreshing
                      // monthly until then.
                      <>
                        Your annual plan is paid through{' '}
                        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
                          {fmtDate(commitmentEndsAt)}
                        </span>
                        . You won't be charged again; it stays active with monthly credit refreshes
                        until then, then ends.
                      </>
                    ) : (
                    // Not renewing — either cancelled, or never a subscription
                    // in the first place. Both end the same way, and neither
                    // is charged again, so one sentence covers both without
                    // telling someone they cancelled when they didn't.
                    <>
                      Active until{' '}
                      <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
                        {fmtDate(subscription.current_period_end)}
                      </span>
                      , then it expires — you will not be charged again. Pick a plan below to keep
                      going with a monthly subscription.
                    </>
                    )
                  )
                )}
              </p>
              {/* Free Trial's caps, spelled out here — the plan comparison grid
                  below never lists the trial (it isn't purchasable, so it has
                  no card there), so this is the one place a trial account can
                  see what it actually gets. */}
              {isTrial && trialLimits && (
                <div className="mt-4 pt-4 border-t border-border/40 dark:border-white/[0.06] grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {[
                    `${subscription.plan.included_credits} AI credits`,
                    `${trialLimits.contacts} contacts`,
                    `${trialLimits.workflows} workflows`,
                    `${trialLimits.campaigns} campaigns`,
                    `${trialLimits.materials} materials`,
                  ].map((text) => (
                    <div key={text} className="flex items-center gap-2 text-foreground/80 dark:text-[#d0d6e0]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      <CheckCircle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc] shrink-0" />
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Seats (Momentum per-seat spec §5, §7, §8): what is paid for,
                  what is in use, and the two ways to change it. Only a
                  per-seat plan has anything to show. */}
              {seatStatus?.per_seat_billing && !STATUS_EXPLAINER[subscription.status] && (
                <div
                  className="mt-4 pt-4 border-t border-border/40 dark:border-white/[0.06] flex items-start justify-between gap-3 flex-wrap"
                  data-testid="plan-seats"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    <Chair className="h-4 w-4 mt-0.5 text-[#5e6ad2] dark:text-[#a5b4fc] shrink-0" weight="fill" />
                    <div className="min-w-0">
                      <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                        {describeSeatUsage(seatStatus)}
                      </p>
                      <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.55 }}>
                        {seatStatus.pending_paid_seats !== null ? (
                          <>
                            From {fmtDate(subscription.current_period_end)} you pay for {seatStatus.pending_paid_seats} seats
                            {' '}({formatCents(seatPurchaseTotalCents(
                              (subscription.billing_cycle === 'annual'
                                ? subscription.plan.annual_monthly_price_cents
                                : subscription.plan.monthly_price_cents) ?? 0,
                              seatStatus.pending_paid_seats,
                            ))}/month). The seats you have now stay usable until then.
                          </>
                        ) : seatStatus.at_plan_ceiling ? (
                          <>{seatCeilingMessage(seatStatus)}</>
                        ) : (
                          <>
                            Each seat is a licensed login with its own {subscription.plan.included_credits.toLocaleString()} credits a month.
                            {' '}Credits are pooled for the team ({(subscription.plan.included_credits * seatStatus.paid_seats).toLocaleString()} a month); contacts are shared at {subscription.plan.features.max_contacts.toLocaleString()}.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  {canManageSubscription && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {seatStatus.pending_paid_seats !== null ? (
                        <Button variant="outline" size="sm" loading={seatBusy} onClick={undoSeatReduction}>
                          Keep {seatStatus.paid_seats} seats
                        </Button>
                      ) : (
                        minimumSeatsAfterReduction(seatStatus) !== null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReducingTo(minimumSeatsAfterReduction(seatStatus))}
                          >
                            Reduce seats
                          </Button>
                        )
                      )}
                      {seatStatus.can_buy_more && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setBuyingSeats(1)}
                          icon={<UsersThree className="h-4 w-4" weight="regular" />}
                        >
                          Buy more seats
                        </Button>
                      )}
                      {seatStatus.at_plan_ceiling && (
                        <Button variant="outline" size="sm" onClick={() => { window.location.href = quotedPlanMailto('Enterprise') }}>
                          Talk to us
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Cancel/Resume deliberately live on the plan card below, under
                  its "Current plan" button — one place to manage the plan, so
                  the two can never disagree about what state it is in. */}
              {STATUS_EXPLAINER[subscription.status] && (
                <Button variant="outline" size="sm" className="mt-3" onClick={goToPlans}>
                  View plans
                </Button>
              )}
            </Card>
          )}

          {/* Plan Comparison — Stripe-inspired tiered layout. Middle (smb) is
              the "Popular" plan with indigo ambient + sparkle decoration. */}
          <div id="billing-plans" className="scroll-mt-6">
            <h2
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px' }}
            >
              Plans
            </h2>
            {/* The business model stated up front rather than as a footnote.
                Monthly bills each month; annual is one yearly charge (a cheaper per-month
                rate, and saying so is the whole point of this paragraph. */}
            <p
              className="text-muted-foreground dark:text-[#8a8f98] mt-1 mb-4 max-w-2xl"
              style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.55 }}
            >
              {activeCycle === 'annual' ? (
                <>
                  The annual plan is charged <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>once for the whole year</span>{' '}
                  — twelve months at a lower rate, paid up front — and your credits still{' '}
                  <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>refresh every month</span>{' '}
                  across the year. You can upgrade at any time; move to a smaller plan once the year
                  ends. If you cancel, next year's renewal is turned off and the plan keeps working
                  to the end of the year you have already paid for. Choose Monthly if you would
                  rather pay month to month.
                </>
              ) : (
                <>
                  Plans are monthly subscriptions. You are charged today and then once a month until
                  you cancel — cancel any time and the plan keeps working to the end of the month you
                  have already paid for, with no refund for that month. Switching plans takes effect
                  immediately and replaces your current plan; billing follows the new plan from then on.
                </>
              )}
            </p>

            {/* Rate tabs. Radio-group semantics rather than plain buttons so
                the two read as one either/or control to a screen reader. */}
            <div
              role="radiogroup"
              aria-label="Billing rate"
              className="inline-flex items-center gap-1 p-1 mb-4 rounded-lg border border-border/50 bg-muted/40 dark:border-white/[0.08] dark:bg-white/[0.03]"
            >
              {([
                { key: 'monthly' as BillingCycle, label: 'Monthly' },
                { key: 'annual' as BillingCycle, label: 'Annually' },
              ]).map(({ key, label }) => {
                const selected = activeCycle === key
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setPlanCycle(key)}
                    className={cn(
                      'inline-flex items-center gap-2 h-8 px-3 rounded-md transition-colors duration-150',
                      selected
                        ? 'bg-card text-foreground shadow-sm dark:bg-white/[0.10] dark:text-[#f7f8f8]'
                        : 'text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]',
                    )}
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    {label}
                    {key === 'annual' && maxAnnualSaving !== null && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/[0.18] dark:text-emerald-300"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.02em' }}
                      >
                        Save {maxAnnualSaving}%
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
            {queuedPlan && subscription && (
              <div
                className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border/60 bg-muted/40 px-4 py-3 dark:border-white/[0.08] dark:bg-white/[0.03]"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590 }}>
                  Plan queue
                </span>
                <span className="text-muted-foreground dark:text-[#8a8f98]">
                  {subscription.plan.display_name} runs until {fmtDate(subscription.current_period_end)}, then{' '}
                  {queuedPlan.plan.display_name} takes over. Nothing extra is charged until then.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
              {visiblePlans.map((plan) => {
                // The plan they are on right now — marked as such whether or
                // not it renews, so the grid always tells them where they
                // stand. The RATE has to match too: a monthly customer looking
                // at the Annual tab is being offered something they do not
                // have, so marking it current there would both mislead them
                // and disable the button that would move them onto it.
                const isQuoted = plan.is_custom_pricing
                const isCurrent =
                  hasLivePlan
                  && subscription?.plan.id === plan.id
                  // A quoted plan is not sold on a cadence at all, so it is
                  // the current plan on whichever tab you happen to be on —
                  // requiring a cycle match hid the chip on the Annual tab.
                  && (isQuoted || subscription?.billing_cycle === activeCycle)
                // The plan they HELD, now lapsed. `livePlan` already treats a
                // 'canceled' payload as no plan, so this cannot overlap with
                // isCurrent.
                const isExpiredPlan =
                  subscription?.status === 'canceled' && subscription?.plan.id === plan.id
                // Null only for a quoted plan; visiblePlans dropped anything
                // else that has no rate on this cycle.
                const rateCents = planRateCents(plan, activeCycle)
                const savingPercent = annualSavingPercent(plan)
                // ...but only a plan that also renews is a dead end. One that is
                // winding down (or never had an agreement) keeps a live CTA,
                // because re-picking it is exactly how renewal is turned back on
                // — /switch-plan resumes the cancelled agreement, or opens a
                // subscription anchored to the expiry date.
                // No downgrade while the current plan renews. A lower-tier plan
                // (by monthly_price_cents) is locked until the customer cancels;
                // once the plan is winding down or expired (planRenews false) the
                // lower plan is selectable again. Quoted plans have no self-serve
                // rate and are never a downgrade target. Mirrors the server guard.
                const isDowngradeBlocked = isDowngradeLocked({
                  planRenews,
                  isCurrent,
                  isQuoted,
                  targetTierCents: plan.monthly_price_cents,
                  currentTierCents: currentPlanTierCents,
                })
                // This plan is already queued to start when the current one
                // expires — nothing more to buy, show it as queued.
                const isQueued = Boolean(queuedPlan && queuedPlan.plan.id === plan.id)
                // A plan whose seat ceiling is below the team size cannot be
                // taken. Mirrors the server's committed-seats rule; the current
                // plan is never blocked this way (it already fits). Asks the
                // plan's actual ceiling rather than isPerSeatPlan, which is
                // false for an UNLIMITED plan as well as a one-seat one and so
                // blocked Enterprise for any team of 2+.
                const isTeamTooBigForPlan = Boolean(
                  !isCurrent
                  && !isQuoted
                  && planSeatsTooFewFor(plan, teamCommitted),
                )
                const isLocked =
                  (isCurrent && planRenews)
                  || isDowngradeBlocked
                  || isQueued
                  || isTeamTooBigForPlan
                const isPopular = plan.name === 'momentum'
                const allowedSet = new Set(plan.features.allowed_integrations || [])

                const tagline =
                  plan.name === 'starter'    ? 'Just the essentials.' :
                  plan.name === 'basic'      ? 'Best for getting started.' :
                  plan.name === 'momentum'   ? 'For growing teams.' :
                  plan.name === 'enterprise' ? 'Built for scale.' :
                                               ''

                // Value prop ribbon copy — short audience hook per tier.
                const valueProp =
                  plan.name === 'starter'    ? 'Try AI outreach on a lean budget' :
                  plan.name === 'basic'      ? 'Best for solo founders & small teams' :
                  plan.name === 'momentum'   ? 'Ideal for growing teams' :
                  plan.name === 'enterprise' ? 'Built for scale & complex workflows' :
                                               ''

                // Slug and label agree (Starter/Basic slugs swapped to match
                // the pricing sheet on 2026-10-13), but this stays a label map
                // because it names the plan BELOW this one.
                const inheritsFrom =
                  plan.name === 'basic'      ? 'Starter' :
                  plan.name === 'momentum'   ? 'Basic' :
                  plan.name === 'enterprise' ? 'Momentum' :
                                               null

                // Highlight is "armed" by CTA hover/focus and stays armed
                // while the mouse remains anywhere inside the card. Releases
                // only when the mouse leaves the card entirely.
                const isHighlighted = !isLocked && armedPlanId === plan.id

                // CTA variant / label resolver
                const ctaVariant: 'default' | 'outline' =
                  isLocked
                    ? 'outline'
                    : isHighlighted
                      ? 'default'
                      : hasLivePlan && currentPlanRateCents !== null && rateCents !== null
                        ? (rateCents > currentPlanRateCents ? 'default' : 'outline')
                        : 'outline'

                const ctaLabel =
                  isQuoted
                    ? 'Talk to us'
                    : isCurrent
                    // Their own plan: nothing to do while it renews, otherwise
                    // the one useful action is putting it back on renewal. The
                    // header chip already says "Current plan" (AB#22386 --
                    // the disabled button used to repeat the same words).
                    ? (planRenews ? 'No action needed' : 'Renew monthly')
                    // Already lined up behind the current plan.
                    : isQueued
                    ? `Starts on ${fmtDate(queuedPlan!.starts_at)}`
                    // The team is larger than this plan can hold.
                    : isTeamTooBigForPlan
                    ? teamTooBigCopy(plan, teamCommitted).label
                    // A lower tier while the current plan renews is not on offer
                    // — the way down is to cancel first.
                    : isDowngradeBlocked
                    ? 'Cancel plan to switch'
                    // Only upgrades are offered against a live plan now; a lower
                    // tier never reaches here (isDowngradeBlocked caught it).
                    : hasLivePlan && currentPlanRateCents !== null && rateCents !== null
                        ? (rateCents > currentPlanRateCents ? 'Upgrade' : 'Subscribe')
                        : 'Subscribe'

                // The up-arrow only reads correctly against a live plan, and
                // only upgrades are actionable now — a blocked lower tier gets
                // no arrow (there is no downgrade to point at).
                const ctaIcon = isCurrent || isQuoted || !hasLivePlan || isDowngradeBlocked
                  || currentPlanRateCents === null || rateCents === null
                  || rateCents <= currentPlanRateCents
                  ? undefined
                  : <ArrowCircleUp className="h-4 w-4" weight="regular" />

                return (
                  <motion.div
                    key={plan.id}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    onMouseEnter={() => !isLocked && setArmedPlanId(plan.id)}
                    onMouseLeave={() => {
                      if (armedPlanId === plan.id) setArmedPlanId(null)
                    }}
                    className={cn(
                      // `duration-280` was not a Tailwind class and the config has
                      // no `transitionDuration` extend, so this transition never
                      // ran at all. `duration-300` is the nearest real step.
                      'relative flex flex-col rounded-2xl border overflow-hidden transition-[background-color,border-color,box-shadow] duration-300 ease-out',
                      isHighlighted
                        ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.04] dark:border-[#7170ff]/40 dark:bg-[#7170ff]/[0.05] shadow-[0_8px_30px_-12px_rgba(94,106,210,0.25)] dark:shadow-[0_10px_36px_-14px_rgba(113,112,255,0.40)]'
                        : 'border-border/50 bg-card dark:border-white/[0.08] dark:bg-white/[0.02]',
                    )}
                  >
                    {/* Value-prop ribbon — pops out from the top on CTA hover.
                        Smooth height + opacity expand pushes card content down.
                        `overflow-hidden` (parent) clips during transition. */}
                    <AnimatePresence initial={false}>
                      {isHighlighted && (
                        <motion.div
                          key="value-prop-ribbon"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div
                            className="bg-[#5e6ad2]/12 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.20] dark:text-[#a5b4fc] py-2 px-4 text-center border-b border-[#5e6ad2]/15 dark:border-[#5e6ad2]/25"
                            style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '0.04em' }}
                          >
                            {valueProp}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="relative z-10 p-5 flex flex-col flex-1">
                      {/* Plan name + permanent Popular chip on SMB
                          (marketing label, independent of subscription/hover). */}
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <h3
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                        >
                          {plan.display_name}
                        </h3>
                        {/* "Current plan" outranks "Popular": which plan you
                            are actually on is the fact you came here for, and on
                            a non-renewing plan the CTA no longer says it. */}
                        {isExpiredPlan ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full border bg-red-500/12 text-red-700 border-red-500/30 dark:bg-red-500/[0.18] dark:text-red-300 dark:border-red-500/45 uppercase whitespace-nowrap"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                          >
                            Plan expired
                          </span>
                        ) : isCurrent ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full border bg-emerald-500/12 text-emerald-700 border-emerald-500/30 dark:bg-emerald-500/[0.18] dark:text-emerald-300 dark:border-emerald-500/45 uppercase whitespace-nowrap"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                          >
                            Current plan
                          </span>
                        ) : isPopular ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full border bg-[#5e6ad2]/12 text-[#5e6ad2] border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.20] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/45 uppercase"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                          >
                            Popular
                          </span>
                        ) : null}
                      </div>

                      {/* Price */}
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span
                          className={cn(
                            'text-foreground dark:text-[#f7f8f8]',
                            !isQuoted && 'tabular-nums',
                          )}
                          style={{
                            fontSize: isQuoted ? '30px' : '42px',
                            fontWeight: 590,
                            letterSpacing: '-0.8px',
                            lineHeight: 1.0,
                          }}
                        >
                          {isQuoted || rateCents === null ? 'Custom' : formatCents(rateCents)}
                        </span>
                        {!isQuoted && (
                          <span
                            className="text-muted-foreground dark:text-[#8a8f98]"
                            style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                          >
                            {isPerSeatPlan(plan) ? 'per seat / month' : 'per month'}
                          </span>
                        )}
                      </div>
                      {/* Annual is one yearly charge, so the card spells out the
                          per-year total that the per-month figure above adds up
                          to — the misread this must not leave open is "annual =
                          a monthly charge". */}
                      {!isQuoted && activeCycle === 'annual' && plan.annual_monthly_price_cents && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] mb-1"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Billed yearly · {formatCents(plan.annual_monthly_price_cents * 12)}
                          {isPerSeatPlan(plan) ? ' per seat / year' : ' / year'}
                        </p>
                      )}
                      {/* Momentum per-seat spec §1: the misreading this
                          feature exists to end is "$129 for up to 5 users".
                          So the card says how seats work right under the
                          price. */}
                      {isPerSeatPlan(plan) && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] mb-1"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          {plan.seat_min}–{plan.seat_max} seats · {rateCents !== null ? `${formatCents(rateCents)} × seats` : ''}
                        </p>
                      )}
                      {/* On the annual tab, say outright that this is still a
                          monthly charge and show what it beats — the confusion
                          this whole feature invites is "annual = one big
                          charge", so the card refuses to leave it open. */}
                      {isQuoted && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] mb-1"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          {QUOTED_PLAN_HINT}
                        </p>
                      )}
                      {activeCycle === 'annual' && !isQuoted && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] mb-1"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          billed monthly · 12-month commitment
                          {savingPercent !== null && (
                            <>
                              {' '}·{' '}
                              <span className="text-emerald-700 dark:text-emerald-300">
                                save {savingPercent}%
                              </span>
                              {' '}vs{' '}
                              <span className="tabular-nums line-through">
                                {formatCents(plan.monthly_price_cents)}
                              </span>
                            </>
                          )}
                        </p>
                      )}

                      {/* Tagline */}
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] mb-4"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                      >
                        {tagline}
                      </p>

                      {/* CTA — full-width. Hover trigger handled at the card
                          level; focus still arms for keyboard accessibility. */}
                      <Button
                        className={cn('w-full', isLocked ? 'mb-2' : 'mb-5')}
                        variant={ctaVariant}
                        // A quoted plan has nothing to buy: opening the
                        // purchase modal would walk the customer to a checkout
                        // the API refuses with a 400.
                        onClick={() => {
                          // A quoted plan has nothing to buy — the button is
                          // how the customer reaches us, active plan or not.
                          if (isQuoted) {
                            window.location.href = quotedPlanMailto(plan.display_name)
                            return
                          }
                          if (!isLocked) openPlanConfirm(plan)
                        }}
                        loading={loadingPlanId === plan.id}
                        // "Talk to us" stays enabled for everyone who can see
                        // this page — it sends an email, it does not spend
                        // money, so it is not gated on manage_subscription.
                        disabled={
                          isQuoted
                            ? false
                            : !canManageSubscription || isLocked
                              || (loadingPlanId !== null && loadingPlanId !== plan.id)
                        }
                        title={
                          isQuoted
                            ? QUOTED_PLAN_HINT
                            : isQueued
                              ? `Queued. Starts when ${subscription?.plan.display_name ?? 'your current plan'} ends on ${fmtDate(queuedPlan!.starts_at)}.`
                              : isTeamTooBigForPlan
                                ? teamTooBigCopy(plan, teamCommitted).hint
                                : isDowngradeBlocked
                                  ? `Cancel ${subscription?.plan.display_name ?? 'your current plan'} first. It stays active until it expires, then you can move to a smaller plan. Upgrades are available any time.`
                                  : undefined
                        }
                        icon={ctaIcon}
                        onFocus={() => !isLocked && setArmedPlanId(plan.id)}
                        onBlur={() => {
                          if (armedPlanId === plan.id) setArmedPlanId(null)
                        }}
                      >
                        {ctaLabel}
                      </Button>

                      {/* Sits directly under "Current plan" so the way out is
                          where the plan is. Same permission as buying: whoever
                          can start a subscription can stop it. Once cancelled
                          it disappears — the plan simply runs out its paid
                          month and the normal buttons come back after that. */}
                      {/* Never for a quoted plan: it is allocated by our team,
                          not bought, so there is no self-serve subscription to
                          stop — and these accounts can still carry an old
                          Stripe row from before they were moved onto a quote,
                          which would otherwise light this up. */}
                      {isCurrent && !isQuoted && canManageSubscription && planRenews && (
                        <Button
                          className="w-full mb-5"
                          variant="outline"
                          onClick={() => setConfirmingCancel(true)}
                          loading={cancelBusy}
                        >
                          Cancel subscription
                        </Button>
                      )}

                      {/* Hairline divider */}
                      <div className="-mx-5 h-px bg-border/50 dark:bg-white/[0.06] mb-4" />

                      {/* Features eyebrow + inheritance line */}
                      <div className="mb-3">
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-1"
                          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                        >
                          Features
                        </p>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98]"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                        >
                          {inheritsFrom
                            ? <>Everything in{' '}<span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590 }}>{inheritsFrom}</span>, plus…</>
                            : 'Core plan features.'}
                        </p>
                      </div>

                      {/* Included credits — first feature, accented */}
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc] shrink-0" />
                        <span
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                        >
                          {/* A listed plan's allowance belongs to the ACCOUNT, so
                              it is printed as-is. A per-seat plan (Enterprise)
                              still multiplies by the seat count, and the card has
                              to say which it is or the figure is a guess. */}
                          {plan.credits_per_user_wallet ? (
                            <>
                              <span className="tabular-nums">{plan.included_credits.toLocaleString()}</span> credits per seat, per month
                              <span className="text-muted-foreground dark:text-[#8a8f98]"> (each user's own)</span>
                            </>
                          ) : plan.credits_are_per_seat ? (
                            <>
                              <span className="tabular-nums">{totalGrantedCredits(plan.included_credits, seatCount).toLocaleString()}</span> credits per month
                              <span className="text-muted-foreground dark:text-[#8a8f98]"> ({plan.included_credits.toLocaleString()}/seat)</span>
                            </>
                          ) : (
                            <>
                              <span className="tabular-nums">{plan.included_credits.toLocaleString()}</span> credits per month
                              <span className="text-muted-foreground dark:text-[#8a8f98]"> (shared by the team)</span>
                            </>
                          )}
                        </span>
                      </div>

                      {/* Feature list.
                          The capability rows come from `plan.plan_features`,
                          which the API derives from the same
                          plan_gating.PLAN_FEATURES table the backend gates
                          on. That is the point: a hardcoded per-tier list
                          here would promise a feature the API refuses (or
                          hide one it allows) the first time a feature moves
                          tier. lib/planFeatures.ts owns only the wording. */}
                      <div className="space-y-2 mb-5">
                        {[
                          { check: true, text: plan.features.max_contacts === -1
                              ? 'Unlimited contacts'
                              : plan.contacts_per_seat
                                ? `${plan.features.max_contacts.toLocaleString()} contacts per seat, pooled for the team`
                                : plan.seat_max !== 1
                                  ? `${plan.features.max_contacts.toLocaleString()} contacts, shared by the whole team`
                                  : `${plan.features.max_contacts.toLocaleString()} contacts` },
                          { check: true, text: `${plan.features.max_campaigns === -1 ? 'Unlimited' : plan.features.max_campaigns} campaigns` },
                          { check: true, text: `${plan.features.max_sender_emails === -1 ? 'Unlimited' : plan.features.max_sender_emails} sender emails` },
                          ...PLAN_CARD_FEATURES.map((key) => ({
                            check: (plan.plan_features || []).includes(key),
                            text: PLAN_FEATURE_LABELS[key],
                          })),
                          { check: plan.features.custom_branding, text: 'Custom branding' },
                          { check: plan.features.priority_support, text: 'Priority support' },
                          { check: plan.features.api_access, text: 'API access' },
                        ].map(({ check, text }, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center gap-2',
                              check ? 'text-foreground dark:text-[#d0d6e0]' : 'text-muted-foreground/60 dark:text-[#62666d]',
                            )}
                            style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                          >
                            <CheckCircle
                              className={cn(
                                'h-4 w-4 shrink-0',
                                check
                                  ? 'text-[#5e6ad2] dark:text-[#a5b4fc]'
                                  : 'text-muted-foreground/30 dark:text-[#3e3e44]',
                              )}
                            />
                            <span>{text}</span>
                          </div>
                        ))}
                      </div>

                      {/* Integrations sub-section */}
                      <div className="mt-auto border-t pt-3 border-border/50 dark:border-white/[0.06]">
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-2"
                          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                        >
                          Integrations{' '}
                          <span className="text-foreground/70 dark:text-[#d0d6e0] tabular-nums" style={{ fontWeight: 590 }}>
                            · {allowedSet.size} included
                          </span>
                        </p>
                        <div className="space-y-1.5">
                          {Object.entries(INTEGRATION_CATEGORIES).map(([catKey, cat]) => (
                            <div key={catKey}>
                              <p
                                className="text-muted-foreground/80 dark:text-[#62666d] uppercase mb-0.5"
                                style={{ fontSize: '9px', fontWeight: 590, letterSpacing: '0.06em' }}
                              >
                                {cat.label}
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {cat.ids.map(id => {
                                  const enabled = allowedSet.has(id)
                                  return (
                                    <span
                                      key={id}
                                      className={cn(
                                        'inline-flex items-center px-1.5 py-0.5 rounded-full border',
                                        enabled
                                          ? 'bg-muted/60 text-foreground/80 border-border/50 dark:bg-white/[0.04] dark:text-[#d0d6e0] dark:border-white/[0.06]'
                                          : 'bg-transparent text-muted-foreground/40 border-border/30 line-through dark:text-[#3e3e44] dark:border-white/[0.04]',
                                      )}
                                      style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.05px' }}
                                    >
                                      {INTEGRATION_LABELS[id] || id}
                                    </span>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Credit Packs */}
          {subscription && (
            <div id="billing-credit-packs" className="scroll-mt-6">
              <h2
                className="text-foreground dark:text-[#f7f8f8]"
                style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px' }}
              >
                Buy more credits
              </h2>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-1 mb-4 max-w-2xl"
                style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.55 }}
              >
                One-time top-ups on top of your plan. Credits land on your balance as soon as payment
                clears — these are always a single charge and never renew.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {creditPacks.map((pack) => {
                  const isRecommended = pack.id === recommendedPackId
                  return (
                  <motion.div
                    key={pack.id}
                    whileHover={{ y: -2, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } }}
                    className={cn(
                      'group relative rounded-xl border p-5 text-center transition-colors duration-200',
                      isRecommended
                        ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.04] ring-1 ring-[#5e6ad2]/40 hover:border-[#5e6ad2]/55 dark:border-[#7170ff]/40 dark:bg-[#7170ff]/[0.06] dark:ring-[#7170ff]/40 dark:hover:border-[#7170ff]/55'
                        : 'bg-card border-border/50 hover:border-[#5e6ad2]/30 hover:bg-[#5e6ad2]/[0.03] dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:border-[#7170ff]/30 dark:hover:bg-white/[0.04]',
                    )}
                  >
                    {isRecommended && (
                      <span
                        className="absolute left-1/2 -top-2.5 -translate-x-1/2 rounded-full border border-[#5e6ad2]/30 bg-[#e7e9f9] px-2.5 py-0.5 text-[#5e6ad2] dark:border-[#7170ff]/40 dark:bg-[#23233a] dark:text-[#9b9bff]"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.2px', textTransform: 'uppercase' }}
                      >
                        Best Value
                      </span>
                    )}
                    <div
                      className="h-10 w-10 rounded-md flex items-center justify-center mx-auto mb-3 bg-emerald-500/10 dark:bg-emerald-500/[0.10]"
                      aria-hidden="true"
                    >
                      <Package className="h-5 w-5 text-emerald-600 dark:text-emerald-400" weight="regular" />
                    </div>
                    <p
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                      style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                    >
                      {pack.credits.toLocaleString()} credits
                    </p>
                    <p
                      className="text-foreground dark:text-[#f7f8f8] mt-1 tabular-nums"
                      style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.4px', lineHeight: 1.1 }}
                    >
                      {formatCents(pack.price_cents)}
                    </p>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums"
                      style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                    >
                      {formatPerCreditRate(pack.price_cents, pack.credits)}
                    </p>
                    <Button
                      className="w-full mt-4"
                      variant={isRecommended ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPendingPack(pack)}
                      loading={purchasingPackId === pack.id}
                      disabled={!canBuyCredits || (purchasingPackId !== null && purchasingPackId !== pack.id)}
                    >
                      Buy {pack.credits.toLocaleString()} credits
                    </Button>
                  </motion.div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== USAGE TAB ===== */}
      {activeTab === 'usage' && (
        <div className="space-y-5">
          {/* Every teammate's credit picture (Analyst + Owner/Admin, via
              credit_budgets.view_usage "all"). On Momentum each row is that
              person's OWN seat wallet (per-seat spec §10); on a pooled plan the
              wallet columns describe the shared pool and say so. Empty when
              the plan has no per-user budgets (Starter) — the API 403s and the
              store keeps an empty list. */}
          {teamWideUsage && teamUsage.length > 0 && (
            <Card className={cardCls} padding="lg" data-testid="team-credit-usage">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30" aria-hidden="true">
                  <UsersThree className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                </div>
                <div>
                  <h2 className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>
                    Credit usage by teammate
                  </h2>
                  <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>
                    {teamUsage.some((r) => r.own_wallet)
                      ? 'Each seat has its own monthly credits; spend this month is per person.'
                      : 'Spend this month per person, drawn from the shared pool.'}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                  <thead>
                    <tr className="text-muted-foreground dark:text-[#8a8f98] text-left uppercase" style={eyebrowStyle}>
                      <th className="py-2 pr-4 font-medium">Teammate</th>
                      <th className="py-2 pr-4 font-medium">Role</th>
                      <th className="py-2 pr-4 font-medium text-right">Used this month</th>
                      <th className="py-2 pr-4 font-medium text-right">Wallet remaining</th>
                      <th className="py-2 font-medium text-right">Monthly cap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamUsage.map((row) => (
                      <tr key={row.user_id} className="border-t border-border/40 dark:border-white/[0.06]">
                        <td className="py-2.5 pr-4">
                          <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontWeight: 510 }}>{row.full_name || row.email}</p>
                          {row.full_name && <p className="text-muted-foreground dark:text-[#8a8f98] truncate">{row.email}</p>}
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground dark:text-[#8a8f98]">{titleizeKey(row.role)}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-foreground dark:text-[#f7f8f8]">{(row.monthly_usage ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-foreground dark:text-[#f7f8f8]">
                          {/* `== null` on purpose: a backend still on the previous
                              release sends no wallet fields at all (undefined),
                              and the frontend deploys independently of it. */}
                          {row.wallet_remaining == null
                            ? '—'
                            : <>{row.wallet_remaining.toLocaleString()}{row.wallet_included != null && <span className="text-muted-foreground dark:text-[#8a8f98]"> / {row.wallet_included.toLocaleString()}</span>}{!row.own_wallet && <span className="text-muted-foreground dark:text-[#8a8f98]"> (pool)</span>}</>}
                        </td>
                        <td className="py-2.5 text-right tabular-nums text-muted-foreground dark:text-[#8a8f98]">
                          {row.monthly_cap == null ? 'No cap' : row.monthly_cap.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {/* Usage-only roles never see the Overview tab, so the pool balance —
              the context every number below is a slice of — is surfaced here. */}
          {!billingAllowed && credits && (
            <Card className={cardCls} padding="lg">
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
                {[
                  { label: 'Remaining', value: totalRemainingCredits(credits) },
                  { label: 'Used this period', value: credits.used_credits },
                  { label: 'Granted', value: creditsGranted(credits) },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] uppercase"
                      style={eyebrowStyle}
                    >
                      {label}
                    </p>
                    <p
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums mt-1"
                      style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.4px', lineHeight: 1.1 }}
                    >
                      {value.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-4"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                One pool, shared by everyone in the account — the figures below are
                the whole team&apos;s spend against it.
              </p>
            </Card>
          )}
          {summary.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {summary.map((item) => {
                const Icon = ACTION_ICONS[item.action] || Lightning
                const tint = ACTION_TINT[item.action] || ACTION_TINT.llm_test
                return (
                  <div key={item.action} className={cn(cardCls, 'p-5')}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={cn('h-9 w-9 rounded-md flex items-center justify-center', tint.wrap)}>
                        <Icon className={cn('h-4 w-4', tint.icon)} weight="regular" />
                      </div>
                      <p
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {ACTION_LABELS[item.action] || item.action}
                      </p>
                    </div>
                    <p
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                      style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.4px', lineHeight: 1.1 }}
                    >
                      {item.total_credits}{' '}
                      <span
                        className="text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        credits
                      </span>
                    </p>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] mt-1 tabular-nums"
                      style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                    >
                      {item.count} actions · avg {item.avg_tokens} tokens
                    </p>
                  </div>
                )
              })}
            </div>
          ) : (
            <Card className={cardCls} padding="lg">
              <div className="text-center py-8 max-w-md mx-auto">
                <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                  <TrendUp className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                </div>
                <h3
                  className="text-foreground dark:text-[#f7f8f8] mb-2"
                  style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                >
                  No usage data yet
                </h3>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mb-6 leading-relaxed"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                >
                  Usage analytics appear here once you start spending credits. In the meantime you can
                  work out what a 30-day window will cost you.
                </p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setActiveTab('estimator')}
                  icon={<Calculator className="h-4 w-4" weight="regular" />}
                >
                  Estimate my usage
                </Button>
              </div>
            </Card>
          )}

          {dailyUsage.length > 0 && (
            <Card className={cardCls} padding="none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
                  <Clock className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" weight="regular" /> Daily usage
                </CardTitle>
                <CardDescription className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                  Last 30 days
                </CardDescription>
              </CardHeader>
              <CardContent className="px-0 pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/50 dark:border-white/[0.06]">
                        {[
                          { label: 'Date',    align: 'left'  as const },
                          { label: 'Credits', align: 'right' as const },
                          { label: 'Tokens',  align: 'right' as const },
                          { label: 'Actions', align: 'right' as const },
                        ].map((h, i, arr) => (
                          <th
                            key={h.label}
                            className={cn(
                              'px-4 py-3 align-middle text-muted-foreground dark:text-[#8a8f98] uppercase',
                              h.align === 'right' ? 'text-right' : 'text-left',
                              i === 0 && 'pl-6',
                              i === arr.length - 1 && 'pr-6',
                            )}
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
                      {dailyUsage.map((day) => (
                        <tr
                          key={day.date}
                          className="hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors duration-150"
                        >
                          <td className="pl-6 pr-4 py-3 text-foreground dark:text-[#f7f8f8] tabular-nums"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                            {day.date}
                          </td>
                          <td className="px-4 py-3 text-right text-foreground dark:text-[#f7f8f8] tabular-nums"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                            {day.credits}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {day.tokens.toLocaleString()}
                          </td>
                          <td className="pl-4 pr-6 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {day.actions}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {breakdown.length > 0 && (
            <Card className={cardCls} padding="none">
              <CardHeader>
                <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
                  Breakdown by action
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/50 dark:border-white/[0.06]">
                        {[
                          { label: 'Action',     align: 'left'  as const },
                          { label: 'Count',      align: 'right' as const },
                          { label: 'Base',       align: 'right' as const },
                          { label: 'Surplus',    align: 'right' as const },
                          { label: 'Total',      align: 'right' as const },
                          { label: 'Avg tokens', align: 'right' as const },
                        ].map((h, i, arr) => (
                          <th
                            key={h.label}
                            className={cn(
                              'px-4 py-3 align-middle text-muted-foreground dark:text-[#8a8f98] uppercase',
                              h.align === 'right' ? 'text-right' : 'text-left',
                              i === 0 && 'pl-6',
                              i === arr.length - 1 && 'pr-6',
                            )}
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
                      {breakdown.map((item) => (
                        <tr
                          key={item.action}
                          className="hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors duration-150"
                        >
                          <td className="pl-6 pr-4 py-3 text-foreground dark:text-[#f7f8f8]"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                            {ACTION_LABELS[item.action] || item.action}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {item.count}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {item.total_base}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {item.total_surplus}
                          </td>
                          <td className="px-4 py-3 text-right text-foreground dark:text-[#f7f8f8] tabular-nums"
                              style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.1px' }}>
                            {item.total_credits}
                          </td>
                          <td className="pl-4 pr-6 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {item.avg_tokens.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {trends.length > 0 && (
            <div>
              <h2
                className="text-foreground dark:text-[#f7f8f8] mb-4"
                style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px' }}
              >
                4-week trends
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {trends.map((t) => {
                  const Icon = ACTION_ICONS[t.action] || Lightning
                  const tint = ACTION_TINT[t.action] || ACTION_TINT.llm_test
                  return (
                    <div key={t.action} className={cn(cardCls, 'p-4')}>
                      <div className="flex items-center gap-3">
                        <div className={cn('h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0', tint.wrap)}>
                          <Icon className={cn('h-4 w-4', tint.icon)} weight="regular" />
                        </div>
                        <div className="min-w-0">
                          <p
                            className="text-foreground dark:text-[#f7f8f8]"
                            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                          >
                            {ACTION_LABELS[t.action] || t.action}
                          </p>
                          <p
                            className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums"
                            style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                          >
                            Avg {t.avg_credits} credits/action · {t.avg_tokens} tokens · {t.total_actions} total
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== HISTORY (TRANSACTIONS) TAB ===== */}
      {activeTab === 'transactions' && (
        <Card className={cardCls} padding="none">
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Transaction log
            </CardTitle>
            <CardDescription className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
              {teamWideUsage
                ? "Complete history of credit changes across the account"
                : 'Complete history of credit changes'}
              {/* The columns were unexplained, so a row read as an unsourced
                  number — "base 8" with nothing saying where 8 came from
                  (AB#22035). One sentence covers every row in the table. */}
              <span className="block mt-1">
                Each charge is <strong className="font-medium text-foreground dark:text-[#f7f8f8]">Base</strong>{' '}
                — the flat price of that action — plus{' '}
                <strong className="font-medium text-foreground dark:text-[#f7f8f8]">Surplus</strong>, charged
                only when a model call runs past the action's included token allowance. Together they make{' '}
                <strong className="font-medium text-foreground dark:text-[#f7f8f8]">Credits</strong>.{' '}
                <strong className="font-medium text-foreground dark:text-[#f7f8f8]">Tokens</strong> is what the
                model actually used, and is blank for actions that call no model.
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0 pt-0">
            {transactions.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/50 dark:border-white/[0.06]">
                        {[
                          { label: 'Date',        align: 'left'  as const, hint: undefined as string | undefined },
                          ...(teamWideUsage ? [{ label: 'Member', align: 'left' as const, hint: undefined as string | undefined }] : []),
                          { label: 'Action',      align: 'left'  as const, hint: 'What was charged for.' },
                          { label: 'Description', align: 'left'  as const, hint: 'Where in the product it happened.' },
                          { label: 'Base',        align: 'right' as const, hint: "The action's flat price, before any token surplus." },
                          { label: 'Surplus',     align: 'right' as const, hint: 'Extra credits for a model call that ran past the included token allowance. 0 when it stayed inside.' },
                          { label: 'Credits',     align: 'right' as const, hint: 'Base + Surplus — what came off the balance.' },
                          { label: 'Tokens',      align: 'right' as const, hint: 'Tokens the model used. Blank for actions that call no model.' },
                        ].map((h, i, arr) => (
                          <th
                            key={h.label}
                            title={h.hint}
                            className={cn(
                              'px-4 py-3 align-middle text-muted-foreground dark:text-[#8a8f98] uppercase',
                              h.align === 'right' ? 'text-right' : 'text-left',
                              i === 0 && 'pl-6',
                              i === arr.length - 1 && 'pr-6',
                            )}
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
                      {transactions.map((t) => (
                        <tr
                          key={t.id}
                          className="hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors duration-150"
                        >
                          <td className="pl-6 pr-4 py-3 text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {new Date(t.created_at).toLocaleString()}
                          </td>
                          {teamWideUsage && (
                            <td className="px-4 py-3 text-muted-foreground dark:text-[#8a8f98] max-w-[180px] truncate"
                                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                                title={t.user_email || undefined}>
                              {t.user_name || t.user_email || '—'}
                            </td>
                          )}
                          <td className="px-4 py-3 text-foreground dark:text-[#f7f8f8]"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                            {t.action ? (ACTION_LABELS[t.action] || t.action) : t.type}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground dark:text-[#8a8f98] max-w-[220px] truncate"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                              title={t.description || undefined}>
                            {t.description}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {t.base_credits}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {t.surplus_credits}
                          </td>
                          <td
                            className={cn(
                              'px-4 py-3 text-right tabular-nums',
                              t.credits < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-emerald-600 dark:text-emerald-400',
                            )}
                            style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.1px' }}
                          >
                            {t.credits > 0 ? '+' : ''}{t.credits}
                          </td>
                          <td className="pl-4 pr-6 py-3 text-right text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {t.tokens_total ? (
                              <span title={`${t.tokens_input} in / ${t.tokens_output} out (threshold: ${t.token_threshold})`}>
                                {t.tokens_total.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/40 dark:text-[#3e3e44]">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {transactionsTotal > 0 && (() => {
                  const totalPages = Math.max(1, Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE))
                  const rangeStart = (transactionsPage - 1) * TRANSACTIONS_PAGE_SIZE + 1
                  const rangeEnd = Math.min(transactionsPage * TRANSACTIONS_PAGE_SIZE, transactionsTotal)
                  return (
                    <div className="p-4 border-t border-border/50 dark:border-white/[0.06] flex items-center justify-between">
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        Showing{' '}
                        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
                          {rangeStart}–{rangeEnd}
                        </span>{' '}
                        of{' '}
                        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
                          {transactionsTotal}
                        </span>
                      </p>
                      <div className="flex items-center gap-3">
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Page {transactionsPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={transactionsPage <= 1}
                          onClick={() => goToTransactionsPage(transactionsPage - 1)}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={transactionsPage >= totalPages}
                          onClick={() => goToTransactionsPage(transactionsPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )
                })()}
              </>
            ) : (
              <div className="text-center py-14 px-6 max-w-md mx-auto">
                <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                  <Receipt className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                </div>
                <h3
                  className="text-foreground dark:text-[#f7f8f8] mb-2"
                  style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                >
                  No transactions yet
                </h3>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mb-6 leading-relaxed"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                >
                  Every plan charge, every top-up and every credit you spend gets a row here.
                </p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={goToPlans}
                  icon={<CreditCard className="h-4 w-4" weight="regular" />}
                >
                  View plans
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== ESTIMATOR TAB ===== */}
      {activeTab === 'estimator' && (
        <Card className={cardCls} padding="lg">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="h-10 w-10 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30"
              aria-hidden="true"
            >
              <Calculator className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
            </div>
            <div>
              <h2
                className="text-foreground dark:text-[#f7f8f8]"
                style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
              >
                Credit estimator
              </h2>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                Plan a 30-day window to find the right plan.
              </p>
            </div>
          </div>

          {catalogError && (
            <p
              className="text-amber-600 dark:text-amber-400 mb-4"
              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
            >
              Couldn&apos;t load the credit price list. Reopen this tab to try again.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {estimatorCatalog.map((item) => (
              <div key={item.key}>
                <Input
                  type="number"
                  min={0}
                  label={item.label}
                  icon={ESTIMATOR_ICONS[item.key] ?? <Sparkle className="h-4 w-4" weight="regular" />}
                  value={estimatorInputs[item.key] ?? 0}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    // `parseInt(…) || 0` alone let a typed "-5" through despite
                    // `min={0}`, which asked the API to estimate negative volume.
                    const parsed = parseInt(e.target.value, 10)
                    const next = Number.isFinite(parsed) ? Math.max(0, parsed) : 0
                    setEstimatorInputs((prev) => ({ ...prev, [item.key]: next }))
                  }}
                />
                {/* The live price, straight from the server's cost table — so a
                    reader can sanity-check the total instead of trusting it. */}
                <p
                  className="text-muted-foreground dark:text-[#62666d] mt-1"
                  style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                >
                  {item.credits_per_unit} credit{item.credits_per_unit === 1 ? '' : 's'} each
                  {item.slides_per_block ? ` · per ${item.slides_per_block} slides` : ''}
                  {item.hint ? ` — ${item.hint}` : ''}
                </p>
              </div>
            ))}
          </div>

          {/* Only rendered when a deck line exists, and only it uses this. */}
          {estimatorCatalog.some((i) => i.slides_per_unit) && (
            <div className="mb-5 max-w-xs">
              <Input
                type="number"
                min={1}
                label="Average slides per deck"
                icon={<Package className="h-4 w-4" weight="regular" />}
                value={avgDeckSlides}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const parsed = parseInt(e.target.value, 10)
                  setAvgDeckSlides(Number.isFinite(parsed) ? Math.max(1, parsed) : DEFAULT_DECK_SLIDES)
                }}
              />
              <p
                className="text-muted-foreground dark:text-[#62666d] mt-1"
                style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
              >
                Decks are billed in blocks, so a longer deck costs proportionally more.
              </p>
            </div>
          )}

          <Button
            variant="default"
            onClick={handleEstimate}
            loading={isEstimating}
            disabled={estimatorCatalog.length === 0}
            icon={<Calculator className="h-4 w-4" weight="regular" />}
          >
            Estimate credits
          </Button>

          {/* The estimate arrives asynchronously and replaces nothing visible,
              so it has to announce itself. */}
          <div aria-live="polite" aria-busy={isEstimating}>
          {estimateResult && (
            <div className="mt-5 border-t pt-5 border-border/50 dark:border-white/[0.06]">
              <div
                className={cn(
                  'rounded-lg border p-4 mb-4',
                  'bg-[#5e6ad2]/[0.05] border-[#5e6ad2]/15',
                  'dark:bg-[#5e6ad2]/[0.10] dark:border-[#5e6ad2]/25',
                )}
              >
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-1"
                  style={eyebrowStyle}
                >
                  Estimated usage per month
                </p>
                <p
                  className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                  style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
                >
                  {estimateResult.total_credits.toLocaleString()}{' '}
                  <span
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    credits
                  </span>
                </p>
                {estimateResult.recommended_plan && (
                  <p
                    className="text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1.5"
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    <Check className="h-3.5 w-3.5" weight="bold" />
                    Recommended:{' '}
                    {/* Resolved against the loaded plan catalog — the old
                        `charAt(0).toUpperCase()` rendered the SMB plan "Smb". */}
                    <strong className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590 }}>
                      {resolvePlanLabel(estimateResult.recommended_plan, plans)}
                    </strong>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                {Object.entries(estimateResult.breakdown).map(([key, val]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-muted/30 dark:hover:bg-white/[0.03] transition-colors duration-150"
                  >
                    {/* No `capitalize` class: CSS would capitalize every word and
                        undo both the sentence casing and the acronym handling. */}
                    <span
                      className="text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {/* The server names each line now; titleizeKey is the
                          fallback for a pre-label response. */}
                      {val.label ?? titleizeKey(key)}
                      <span className="text-muted-foreground/70 dark:text-[#62666d]">
                        {' '}· {val.count.toLocaleString()} × {val.per_unit}
                        {val.slide_blocks && val.slide_blocks > 1
                          ? ` (${val.slide_blocks} blocks)`
                          : ''}
                      </span>
                    </span>
                    <span
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {val.count} × {val.per_unit} ={' '}
                      <strong style={{ fontWeight: 590 }}>{val.total}</strong> credits
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </Card>
      )}

      {/* Nothing on this page spends money without passing through here. */}
      <CreditConfirmModal
        isOpen={pendingPurchase !== null}
        actionLabel={pendingPurchase?.actionLabel ?? ''}
        priceCents={pendingPurchase?.priceCents}
        grantedCredits={pendingPurchase?.grantedCredits}
        currentBalance={totalRemaining}
        detail={pendingPurchase?.detail}
        confirmLabel={pendingPurchase?.confirmLabel}
        loading={confirmBusy}
        // Plans are subscriptions (monthly or yearly); credit top-ups never are.
        recurring={pendingPurchase?.recurring === true}
        recurringInterval={pendingPurchase && 'recurringInterval' in pendingPurchase ? pendingPurchase.recurringInterval : 'month'}
        onConfirm={() => pendingPurchase?.onConfirm()}
        onCancel={closeConfirm}
      />

      {/* Buy more seats (spec §7): prorated for the rest of the period and
          charged today; the recurring amount rises from the next invoice. */}
      <ConfirmDialog
        open={buyingSeats !== null}
        title="Buy more seats"
        description={
          buyingSeats !== null && seatStatus && subscription ? (
            <div className="space-y-3">
              <p>
                You pay for {seatStatus.paid_seats} seat{seatStatus.paid_seats === 1 ? '' : 's'} today. {subscription.plan.display_name} allows up to{' '}
                {seatStatus.max_seats ?? 5}; how many more do you need?
              </p>
              <div className="flex items-center gap-3">
                <label htmlFor="buy-extra-seats" className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', fontWeight: 510 }}>Additional seats</label>
                <Input
                  id="buy-extra-seats"
                  type="number"
                  min={1}
                  max={extraSeatsPurchasable(seatStatus)}
                  value={buyingSeats}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10)
                    setBuyingSeats(Number.isNaN(n) ? 1 : Math.min(extraSeatsPurchasable(seatStatus), Math.max(1, n)))
                  }}
                  className="w-24 tabular-nums"
                />
              </div>
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', lineHeight: 1.5 }}>
                Your recurring charge becomes{' '}
                {formatCents(seatPurchaseTotalCents(
                  (subscription.billing_cycle === 'annual'
                    ? subscription.plan.annual_monthly_price_cents
                    : subscription.plan.monthly_price_cents) ?? 0,
                  seatStatus.paid_seats + buyingSeats,
                ) * (subscription.billing_cycle === 'annual' ? 12 : 1))}{subscription.billing_cycle === 'annual' ? '/year' : '/month'}. The remainder of the current period for the new seat{buyingSeats === 1 ? '' : 's'} is charged today, prorated,
                and each new seat gets its own credits as soon as someone fills it.
              </p>
            </div>
          ) : ''
        }
        confirmLabel={buyingSeats ? `Buy ${buyingSeats} seat${buyingSeats === 1 ? '' : 's'}` : 'Buy'}
        loading={seatBusy}
        onConfirm={confirmBuySeats}
        onCancel={() => !seatBusy && setBuyingSeats(null)}
      />

      {/* Reduce seats (spec §8): effective at the next renewal, no refund,
          never below the plan minimum or the people currently seated. */}
      <ConfirmDialog
        open={reducingTo !== null}
        title="Reduce seats"
        description={
          reducingTo !== null && seatStatus && subscription ? (
            <div className="space-y-3">
              <p>
                From {fmtDate(subscription.current_period_end)} you will pay for fewer seats. Nothing is refunded for the
                current period, and everyone keeps their seat until then.
              </p>
              <div className="flex items-center gap-3">
                <label htmlFor="reduce-seats-to" className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', fontWeight: 510 }}>Seats from next renewal</label>
                <Input
                  id="reduce-seats-to"
                  type="number"
                  min={minimumSeatsAfterReduction(seatStatus) ?? seatStatus.min_seats}
                  max={seatStatus.paid_seats - 1}
                  value={reducingTo}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10)
                    const floor = minimumSeatsAfterReduction(seatStatus) ?? seatStatus.min_seats
                    setReducingTo(Number.isNaN(n) ? floor : Math.min(seatStatus.paid_seats - 1, Math.max(floor, n)))
                  }}
                  className="w-24 tabular-nums"
                />
              </div>
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', lineHeight: 1.5 }}>
                {seatStatus.plan_name ? seatStatus.plan_name.charAt(0).toUpperCase() + seatStatus.plan_name.slice(1) : 'This plan'} needs at least {seatStatus.min_seats} seats and {seatStatus.occupied_seats} {seatStatus.occupied_seats === 1 ? 'person holds' : 'people hold'} a seat right now.
                {seatStatus.min_seats > 2 ? ' For a smaller team, switch to Momentum instead.' : ' For a single user, switch to Starter or Basic instead.'}
              </p>
            </div>
          ) : ''
        }
        confirmLabel={reducingTo ? `Reduce to ${reducingTo} seats` : 'Reduce'}
        loading={seatBusy}
        onConfirm={confirmReduceSeats}
        onCancel={() => !seatBusy && setReducingTo(null)}
      />

      {/* Cancelling is destructive-ish but reversible, and the wording has to
          make clear that access is NOT lost today. */}
      <ConfirmDialog
        open={confirmingCancel}
        title="Cancel subscription?"
        description={
          commitmentEndsAt ? (
            // The one place a customer must not be misled. This used to say
            // the monthly charge continued until the commitment end — true
            // under the old model where annual was billed monthly, and badly
            // wrong now that the whole year is charged up front (AB#21561):
            // it warned of payments that will never be taken.
            <>
              Your {subscription?.plan.display_name ?? 'plan'} is on the annual plan, paid for the
              whole year up front. Cancelling turns off next year's renewal —{' '}
              <strong>you will not be charged again</strong>, and the plan keeps working, with
              credits refreshing every month, until{' '}
              <strong>{fmtDate(commitmentEndsAt)}</strong>. You can resume any time before then.
            </>
          ) : (
            <>
              Your {subscription?.plan.display_name ?? 'plan'} stays active until{' '}
              <strong>{fmtDate(subscription?.current_period_end ?? null)}</strong> — the end of the
              month you have already paid for. You keep the credits and features until then and
              nothing is lost today; there is no further charge, and after that date the plan
              expires. You can resume any time before then.
            </>
          )
        }
        confirmLabel="Cancel subscription"
        cancelLabel="Keep my subscription"
        tone="destructive"
        loading={cancelBusy}
        onConfirm={handleCancelRenewal}
        onCancel={() => setConfirmingCancel(false)}
      />
    </div>
  )
}
